import React, { useEffect, useState, useMemo } from 'react';
import Scan from './Scan';
import ScansSkeleton from './ScansSkeleton';
import EmptyTrackingState from './EmptyTrackingState';
import { useSelector } from 'react-redux';
import { 
    EXCLUDED_INSTRUCTIONS, 
    STATUS_CONFIG, 
    ORDER_STATUSES, 
    getDeliveryText
} from './constants';

function setInstructionText(instruction) {
    switch (instruction) {
        case 'Delivered to consignee':
        case 'Delivered to consignee - Code Verified delivery':
            return 'Item delivered';
        case 'Shipment Received at Facility':
            return 'Shipment arrived';
        case 'Office/Institute closed':
            return 'Delivery attempt failed';
        case 'Vehicle Departed': 
            return 'Shipment departed';
        case 'RETURN Accepted':
            return 'Return completed';
        default:
            return instruction;
    }
}

function ScansList() {
    const [lastNotDelivered, setLastNotDelivered] = useState(true);
    const [deliveryLocation, setDeliveryLocation] = useState('');

    const orderDetails = useSelector((state) => state.orderDetails.orderDetails);
    const orderStatus  = useSelector((state) => state.orderDetails.orderStatus);
    const shipmentData = useSelector((state) => state.orderDetails?.shipmentData || null);

    /** Extract scans and last status from nested structure */
    const { scans, lastStatus } = useMemo(() => {
        const shipment = shipmentData?.shipmentData?.data?.data?.ShipmentData?.[0]?.Shipment;
        if (!shipment) {
            return { scans: [], lastStatus: {} };
        }

        const allScans = Array.isArray(shipment.Scans) ? shipment.Scans.slice() : [];

        // find index of last "Out for delivery"
        let lastOfdIndex = -1;
        // first "Pickup scheduled"
        let firstPickupIndex = -1;

        allScans.forEach((s, i) => {
            const instr = s?.ScanDetail?.Instructions;
            if (!instr) return;
            const lower = String(instr).toLowerCase().trim();

            if (lower === "out for delivery") {
                lastOfdIndex = i;
            }

            if (firstPickupIndex === -1 && lower === "pickup scheduled") {
                firstPickupIndex = i;
            }
        });

        const filtered = allScans.filter((s, i) => {
            const instr = s?.ScanDetail?.Instructions;
            if (!instr) return true;
            const lower = String(instr).toLowerCase().trim();

            if (lower === "out for delivery") {
                return i === lastOfdIndex;
            }

            if (lower === "pickup scheduled") {
                return i === firstPickupIndex;
            }

            return true;
        });

        return {
            scans: filtered,
            lastStatus: shipment.Status || {}
        };
    }, [shipmentData]);

    /** Filter unwanted instructions */
    const filteredScans = useMemo(() => {
        if (!scans || scans.length === 0) return [];
        
        const filtered = scans.slice(0, -1).filter(scan =>
            !EXCLUDED_INSTRUCTIONS.includes(scan.ScanDetail.Instructions)
        );

        filtered.push(scans[scans.length - 1]);

        return filtered;
    }, [scans]);

    /** Delivery + Return Flags */
    const isDelivered = useMemo(() => {
        return lastStatus.Instructions === 'Delivered to consignee' || 
               lastStatus.Instructions === 'Delivered to consignee - Code Verified delivery';
    }, [lastStatus]);

    const isReturnCompleted = useMemo(() => {
        return lastStatus.Instructions === 'RETURN Accepted';
    }, [lastStatus]);

    /** Determine if tail is still pending */
    useEffect(() => {
        setLastNotDelivered(!isDelivered && !isReturnCompleted);
    }, [isDelivered, isReturnCompleted]);

    /** Render conditions */

    const shouldShowShippingSoon = 
        filteredScans.length === 0 && 
        orderStatus !== ORDER_STATUSES.CANCELLED && 
        orderStatus !== ORDER_STATUSES.PENDING &&
        orderStatus !== ORDER_STATUSES.READY_FOR_PICKUP &&
        orderStatus !== ORDER_STATUSES.RETURN;

    const shouldShowDeliveryPending = 
        lastNotDelivered &&
        orderStatus !== ORDER_STATUSES.CANCELLED && 
        orderStatus !== ORDER_STATUSES.RETURN && 
        (
            orderStatus === ORDER_STATUSES.PENDING || 
            orderStatus === ORDER_STATUSES.READY_FOR_PICKUP || 
            filteredScans.length > 0
        );

    const shouldShowEmptyState = 
        filteredScans.length === 0 && 
        orderStatus === ORDER_STATUSES.SHIPPED;

    const shouldShowReturnAfterDelivery =
        orderStatus === ORDER_STATUSES.RETURN && isDelivered;

    /** Pending return (not completed, not delivered) */

    const shouldShowPendingReturn =
        orderStatus === ORDER_STATUSES.RETURN &&
        !isReturnCompleted &&
        !shouldShowReturnAfterDelivery;

    /** LOCATION */

    useEffect(() => {
        const shipment = shipmentData?.shipmentData?.data?.data?.ShipmentData?.[0]?.Shipment;
        
        if (!shipment) {
            setDeliveryLocation('');
            return;
        }

        if (shipment.Consignee?.City && shipment.Consignee?.State) {
            setDeliveryLocation(`${shipment.Consignee.City} (${shipment.Consignee.State})`);
            return;
        }

        if (shipment.Destination) {
            setDeliveryLocation(shipment.Destination);
        }
    }, [shipmentData]);

    /** DELIVERY DATE */

    const shipment = shipmentData?.shipmentData?.data?.data?.ShipmentData?.[0]?.Shipment;
    const deliveryDate = shipment?.ExpectedDeliveryDate || 
                         shipment?.PromisedDeliveryDate || 
                         '';

    /** LOADING */

    if (!orderDetails || Object.keys(orderDetails).length === 0) {
        return <ScansSkeleton />;
    }

    if (orderDetails.waybill && !shipmentData) {
        return <ScansSkeleton />;
    }

    if (shouldShowEmptyState) {
        return <EmptyTrackingState orderStatus={orderStatus} />;
    }

    return (
        <div className={`flex ${scans.length > 0 && 'pb-10'}`}>
            {
                scans.length > 0 ? 
                <div className="flex flex-col items-center gap-14" role="list" aria-label="Order tracking timeline">
                    
                    {/* always first */}
                    <Scan 
                        instruction={'Order Placed'} 
                        date={orderDetails?.createdAt || ''} 
                        index={0} 
                    />

                    {shouldShowShippingSoon && (
                        <Scan 
                            instruction={'Shipping soon'} 
                            index={1}
                            isActive={true}
                            isLast={true} 
                            lastNotDelivered={true} 
                            isGreen={true} 
                        />
                    )}

                    {filteredScans.map((scan, index) => {
                        const isActiveScan =
                            scan.ScanDetail.Instructions === lastStatus.Instructions && 
                            scan.ScanDetail.ScanDateTime === lastStatus.StatusDateTime;

                        const isLastDisplayedScan =
                            index === filteredScans.length - 1 &&
                            !shouldShowDeliveryPending &&
                            !shouldShowReturnAfterDelivery &&
                            !isReturnCompleted;

                        const isSecondLast =
                            lastNotDelivered &&
                            index === filteredScans.length - 1 &&
                            (shouldShowDeliveryPending || shouldShowPendingReturn);

                        return (
                            <Scan 
                                key={`${scan.ScanDetail.ScanDateTime}-${index}`}
                                instruction={setInstructionText(scan.ScanDetail.Instructions)} 
                                date={scan.ScanDetail.ScanDateTime} 
                                location={scan.ScanDetail.ScannedLocation} 
                                index={index + 1} 
                                isActive={isActiveScan} 
                                isLast={isLastDisplayedScan} 
                                lastNotDelivered={lastNotDelivered}
                                isSecondLast={isSecondLast} 
                            />
                        );
                    })}

                    {/* DELIVERY PENDING TAIL */}
                    {shouldShowDeliveryPending && (
                        <Scan 
                            instruction={getDeliveryText(deliveryDate)} 
                            index={filteredScans.length + 1} 
                            isLast={true} 
                            lastNotDelivered={false} 
                            isGreen={false} 
                            destination={deliveryLocation} 
                            date={deliveryDate} 
                        />
                    )}

                    {/* CANCELLED */}
                    {orderStatus === ORDER_STATUSES.CANCELLED && (
                        <Scan 
                            instruction={STATUS_CONFIG.CANCELLED.instruction}
                            index={STATUS_CONFIG.CANCELLED.index}
                            isActive={STATUS_CONFIG.CANCELLED.isActive}
                            isLast={true} 
                            lastNotDelivered={false} 
                            isGreen={STATUS_CONFIG.CANCELLED.isGreen} 
                        />
                    )}

                    {/* RETURN AFTER DELIVERY */}
                    {shouldShowReturnAfterDelivery && (
                        <Scan 
                            instruction={'Item returned'}
                            index={filteredScans.length + 1} 
                            isLast={true} 
                            lastNotDelivered={false} 
                            isGreen={true} 
                            isActive={true}
                        />
                    )}

                    {/* RETURN COMPLETED (no delivery) */}
                    {orderStatus === ORDER_STATUSES.RETURN && isReturnCompleted && !shouldShowReturnAfterDelivery && (
                        <Scan 
                            instruction={'Item returned'}
                            index={filteredScans.length + 1}
                            isLast={true}
                            lastNotDelivered={false}
                            isGreen={true}
                            isActive={true}
                        />
                    )}

                    {/* RETURN PENDING */}
                    {shouldShowPendingReturn && (
                        <Scan
                            instruction={'To be returned soon'}
                            index={filteredScans.length + 1}
                            isLast={true}
                            lastNotDelivered={false}
                            isGreen={false}
                            isActive={false}
                        />
                    )}
                </div> : 
                <p className='font-medium text-[14px] leading-6 text-rede5'>
                    Tracking not available.
                </p>
            }
        </div>
    );
}

export default React.memo(ScansList);
